'use client';
import { BuilderBlocks } from '@builder.io/react';
import { useRef, useState } from 'react';
import Slider from "react-slick";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";

interface ProcessSliderProps {
    slides?: any[];
    builderBlock?: {
        id: string;
        children: any[];
    };
    children?: React.ReactNode[];
}

export function ProcessSlider({ slides = [], builderBlock, children }: ProcessSliderProps) {
    const [currentSlide, setCurrentSlide] = useState(0);
    const sliderRef = useRef<Slider>(null);

    const hasMultipleSlides = slides.length > 1 && children && children.length > 1;

    const settings = {
        infinite: hasMultipleSlides,
        speed: 500,
        autoplay: false,
        arrows: false,
        beforeChange: (_: number, next: number) => setCurrentSlide(next),
        slidesToShow: 1,
        slidesToScroll: 1,
    };

    return (
        <div className="relative w-full">
            <Slider ref={sliderRef} {...settings}>
                {children && !!children.length ? 
                    children.map((item, index) => (
                        <div key={index} className="relative">
                            <div className="relative h-[800px] md:h-[600px] md:rounded-2xl overflow-hidden">
                                {slides[index]?.backgroundVideo ? (
                                    <video
                                        autoPlay
                                        muted
                                        loop
                                        playsInline
                                        poster={slides[index].backgroundImage}
                                        className="absolute inset-0 w-full h-full object-cover object-bottom"
                                    >
                                        <source src={slides[index].backgroundVideo} type="video/mp4" />
                                    </video>
                                ) : (
                                    <img 
                                        src={slides[index]?.backgroundImage} 
                                        alt={slides[index]?.title} 
                                        className="w-full h-full object-cover" 
                                    />
                                )}
                                <div 
                                    className="absolute inset-0"
                                    style={{ background: 'linear-gradient(89.82deg, rgba(28, 36, 29, 0.4) 18.61%, rgba(28, 36, 29, 0.8) 61.02%)' }}
                                >
                                    <div className="p-8 text-white h-full">
                                        {item}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )) : 
                    <div className="relative">
                        <div className="relative h-[700px] md:h-[500px] md:rounded-2xl overflow-hidden">
                            <div className="absolute inset-0" style={{ background: 'linear-gradient(89.82deg, rgba(28, 36, 29, 0.5) 18.61%, rgba(28, 36, 29, 0.9) 61.02%)' }}>
                                <div className="p-8 text-white">
                                    <BuilderBlocks
                                        parentElementId={builderBlock?.id}
                                        blocks={builderBlock?.children}
                                        dataPath="this.children"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                }
            </Slider>

            {hasMultipleSlides && (
                <div className="absolute bottom-4 w-full justify-center md:justify-start md:ml-[5%] flex items-center gap-2">
                    <button
                        onClick={() => sliderRef.current?.slickPrev()}
                        className="p-2 text-white transition-all hover:opacity-80"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                            <path fillRule="evenodd" d="M7.72 12.53a.75.75 0 0 1 0-1.06l7.5-7.5a.75.75 0 1 1 1.06 1.06L9.31 12l6.97 6.97a.75.75 0 1 1-1.06 1.06l-7.5-7.5Z" clipRule="evenodd" />
                        </svg>
                    </button>

                    <div className="flex gap-2">
                        {slides.map((slide, index) => (
                            <div
                                key={index}
                                onClick={() => sliderRef.current?.slickGoTo(index)}
                                className="relative cursor-pointer rounded-lg overflow-hidden w-[60px] h-[60px] md:w-[86px] md:h-[86px] border-2 transition-all
                                    ${currentSlide === index ? 'border-blue-500' : 'border-transparent'}"
                            >
                                <img
                                    src={slide.thumbnail}
                                    alt={slide.title}
                                    className="w-full h-full object-cover"
                                />
                                <div 
                                    className={`absolute inset-0 transition-all`}
                                    style={currentSlide !== index ? {
                                        background: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(15, 36, 17, 0.9) 100%)'
                                    } : undefined}
                                />
                                <div 
                                    className={`absolute top-1 left-1 right-1 px-2 py-0.5 rounded-[14px] border border-white text-white text-[10px] text-center transition-all
                                        ${currentSlide === index ? 'bg-fp-green' : 'bg-gray-500/50'}`}
                                >
                                    {slide.title}
                                </div>
                            </div>
                        ))}
                    </div>

                    <button
                        onClick={() => sliderRef.current?.slickNext()}
                        className="p-2 text-white transition-all hover:opacity-80"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                            <path fillRule="evenodd" d="M16.28 11.47a.75.75 0 0 1 0 1.06l-7.5 7.5a.75.75 0 0 1-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 0 1 1.06-1.06l7.5 7.5Z" clipRule="evenodd" />
                        </svg>
                    </button>
                </div>
            )}
        </div>
    );
} 